import * as React from 'react';
import {Component} from 'react';

import {observer} from 'mobx-react';
import {
    Drawer,
    Flex,
    flexColumnProps,
    Title,
    Text,
    Icon,
    PriorityIcon,
    CheckBox,
    Separator
} from '@priority-software/priority-style-react';
import {SearchesDropdown} from './components/SearchesDropdown';
import {
    selectGeneratorSearch,
    resetSearches,
    createNewSearch,
    changeIgnoreCase,
    setTempSearch
} from './actions/searchesList';
import {LLM_REQ_REJECTED} from './consts/searches';
import {formsStore} from '../Form/stores/FormsStore';
import {getLocalizedText, texts} from '../../constants/TextConst';
import {TitleInput} from './components/TitleInput/TitleInput';
import {SearchesFooter} from './components/SearchesFooter';

import {setHasModalChild} from '../../_shared/GWTBridge/invoke/netForm';
import {returnFocus} from '../../_shared/GWTBridge/invoke/genPage';
import {togglePageClass} from '../../_shared/GWTBridge/invoke/headerPanel';
import {setIsNavigationEnabled} from '../../_shared/GWTBridge/invoke/genPage';
import {
    showHelpMessage,
    showShouldExitMessage
} from '../../_shared/Messages/actions/MessageActions';
import {keyCodes} from '../../constants';
import {generatorHelpKey} from './consts/texts';
import {getServerTextByKey} from '../../Localization/texts';
import {isRTL} from '../../Localization';
import {
    getTopDialogZindex,
    updateTopDialogZindex
} from '../../_shared/GWTBridge/invoke/priModalDlg';
import {Label} from '../../components/Common/Label';
import {hoverBtns} from './consts/enums';
import {FiltersList} from './components/FiltersList';
import {getAISuggestedFilters} from '../../services/rest/llm/aiSuggestedFilters';
import {
    setAIFilterValues,
    getPossibleSearchValues,
    supportAISearch
} from './components/AISearch/utils';
import {AISearchBlock} from './components/AISearch/parts/AISearchBlock/AISearchBlock';
import {getUuid4} from '../../utils/getUuid4';
import {RecommendedSearch} from './components/RecommendedSearch';
import {monitorAction} from '../../services/datadog';
import {AIAdvancedSearchMonitoring} from '../../constants/monitoring';

@observer
class SearchesDropdownComp extends Component<{searchesStore}, {isEditing: boolean}> {
    constructor(props) {
        super(props);
        this.state = {
            isEditing: false
        };
        this.startEditing = this.startEditing.bind(this);
        this.finishEditing = this.finishEditing.bind(this);
    }

    startEditing() {
        const {searchesStore} = this.props;
        searchesStore.generatorSelected.setIsNewNameSaved(false);
    }

    finishEditing() {
        const {searchesStore} = this.props;
        searchesStore.generatorSelected.setIsNewNameSaved(true);
    }

    render() {
        const {searchesStore} = this.props;
        const {generatorSelected} = searchesStore;

        if (
            searchesStore.savedSearchesList?.length === 0 &&
            searchesStore.previousList?.length === 0
        ) {
            return (
                <Flex paddingBottom paddingTop justifyContent="flex-start">
                    <Icon
                        componentClasses="search-filter-big"
                        icon={PriorityIcon.filter}
                        skin="standart"
                        counterValue={
                            searchesStore.generatorSelected &&
                            searchesStore.generatorSelected.filtersCounter
                        }
                    />

                    <Text textColor="primary-700" skin="paragraph-2">
                        {searchesStore.generatorSelected
                            ? searchesStore.generatorSelected.name
                            : getLocalizedText(texts.newSearch)}
                    </Text>
                </Flex>
            );
        } else if (generatorSelected && !generatorSelected.isNewNameSaved) {
            return <TitleInput searchesStore={searchesStore} finishEditing={this.finishEditing} />;
        } else {
            return (
                <SearchesDropdown
                    selectedSearch={searchesStore.generatorSelected}
                    hoverCompAction={this.startEditing}
                    searchesStore={searchesStore}
                    onSelect={selectGeneratorSearch}
                    onPreviousSelect={setTempSearch}
                    displayFiltersCounter
                    openingSide="rightSide"
                    hoverBtnsArr={[hoverBtns.Default, hoverBtns.Delete]}
                    isButtonDisabled={
                        searchesStore.generatorSelected && searchesStore.generatorSelected.isNew
                    }
                    onButtonClick={createNewSearch}
                    isShowPrevious={true}
                />
            );
        }
    }
}

type State = {
    open: boolean;
    aiSearch: boolean;
    aiSearchNoResults: boolean;
    aiSearchLoading: boolean;
    aiSearchError: boolean;
    showRecommendedSearch: boolean;
    searchKey: string;
};

type Props = {};

@observer
export class SearchesGenerator extends Component<Props, State> {
    focusRef: React.RefObject<HTMLDivElement> = React.createRef();
    focusTimer: NodeJS.Timer;
    searchBtnRef = React.createRef<HTMLButtonElement>();
    clearedSavedFilters = false;

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            aiSearch: false,
            aiSearchNoResults: false,
            aiSearchLoading: false,
            aiSearchError: false,
            showRecommendedSearch: true,
            searchKey: getUuid4()
        };
    }

    handleKeyDown = e => {
        if (this.state.open) {
            if (e.keyCode === keyCodes.F1) {
                e.preventDefault();
                e.stopPropagation();
                this.onShowHelp();
            }
        }
    };

    open = (isAISearch?: boolean) => {
        const {searchesStore} = formsStore;
        const {generatorSelected} = searchesStore;
        document.addEventListener('keydown', this.handleKeyDown);
        setIsNavigationEnabled(false);
        if (generatorSelected.filters.size !== 0) {
            searchesStore.generatorSelected.resetIsDirty();
        }
        setHasModalChild(true);
        updateTopDialogZindex(true);
        this.setState({
            open: true,
            aiSearch: isAISearch,
            aiSearchError: false,
            aiSearchNoResults: false
        });
    };

    hide = (focus = true) => {
        this.setState({open: false});
        updateTopDialogZindex(false);
        document.removeEventListener('keydown', this.handleKeyDown);
        setIsNavigationEnabled(true);
        clearTimeout(this.focusTimer);
        const {searchesStore} = formsStore;
        // we need to wait for closing animation to end
        // before reset the search,
        // because otherwise the new selected will be shown
        setTimeout(() => resetSearches(searchesStore), 400);
        togglePageClass(false);
        setHasModalChild(false);
        focus && returnFocus();
    };

    onPanelClose = () => {
        const {searchesStore} = formsStore;
        const closeCallback = () => {
            this.hide();
            this.toggleAiSearch(false);
        };
        if (searchesStore.generatorSelected.isDirty) {
            showShouldExitMessage(closeCallback);
        } else {
            closeCallback();
        }
    };

    toggleIgnoreCase = () => {
        const {searchesStore} = formsStore;
        const {generatorSelected} = searchesStore;
        changeIgnoreCase(generatorSelected);
    };

    returnPanelFocus = () => {
        this.focusRef.current.focus();
    };

    onShowHelp = () => {
        getServerTextByKey(generatorHelpKey).then(res => {
            showHelpMessage(res, this.returnPanelFocus);
        });
    };

    focusSearchBtn = () => {
        this.searchBtnRef?.current?.focus();
    };

    toggleAiSearch = (flag: boolean) => {
        this.setState({aiSearch: flag});
    };

    showAiSearch = () => {
        const {searchesStore} = formsStore;
        searchesStore.generatorSelected.clearFilters();
        this.toggleAiSearch(true);
        monitorAction(AIAdvancedSearchMonitoring.aiSearchButtonClicked);
    };

    handleAiSearch = async (value: string) => {
        this.setState({aiSearchLoading: true, aiSearchNoResults: false, aiSearchError: false});
        const {searchesStore} = formsStore;

        try {
            searchesStore.generatorSelected.clearFilters();
            const res = await getAISuggestedFilters({
                formName: formsStore.currentForm.title,
                query: value,
                columns: formsStore.currentForm.columns.map(c => ({
                    name: c.name,
                    title: c.title,
                    type: c.code,
                    possibleValues: getPossibleSearchValues(c)
                }))
            });
            if (!res.filters?.length || res.code === LLM_REQ_REJECTED) {
                monitorAction(AIAdvancedSearchMonitoring.aiSearchNoResults);
            }
            if (res?.filters?.length) {
                await setAIFilterValues(
                    searchesStore.generatorSelected,
                    formsStore.currentForm.columns,
                    res.filters
                );
            } else {
                if (res?.code === LLM_REQ_REJECTED || res?.code === 2) {
                    searchesStore.generatorSelected.clearFilters();
                    this.setState({aiSearchNoResults: true});
                }
            }
        } catch (e) {
            searchesStore.generatorSelected.clearFilters();
            this.setState({aiSearchNoResults: true, aiSearchError: true});
            console.error(e);
        } finally {
            this.setState({aiSearchLoading: false, aiSearchError: false});
        }
    };

    onClear = () => {
        this.clearedSavedFilters = true;
        this.setState({
            searchKey: getUuid4()
        });
    };

    toggleRecommendedSearch = () => {
        const isOpen = this.state.showRecommendedSearch;
        this.setState({showRecommendedSearch: !isOpen});
    };

    hideRecommendedSearch = () => {
        this.setState({showRecommendedSearch: false});
    };

    render() {
        const {aiSearchNoResults, aiSearchLoading, aiSearchError} = this.state;
        const {searchesStore, currentForm} = formsStore;
        const {generatorSelected, recommendedSearch} = searchesStore;
        const title = currentForm && currentForm.title ? `${currentForm.title}` : '';
        const hasFilters = generatorSelected && generatorSelected.filters.size > 0;
        const isAISearchSupported = supportAISearch(this.clearedSavedFilters);

        return (
            <Drawer
                open={this.state.open}
                maskClosable={false}
                rtl={isRTL()}
                zIndex={getTopDialogZindex()}
                componentClasses="reactComp"
                width={620}
            >
                <div tabIndex={-1} ref={this.focusRef} />

                <div className="generator-content">
                    <Flex {...flexColumnProps} componentClasses="header">
                        <Flex justifyContent="space-between" componentClasses="header-top-part">
                            <Title skin="h2" textColor="primary-700" componentClasses={'ellipsis'}>
                                {getLocalizedText(texts.advancedSearch)}&nbsp;
                                <span className="search-form-title">{title}</span>
                            </Title>
                            <Flex justifyContent="flex-end" style={{width: 'fit-content'}}>
                                <Icon
                                    skin="standart"
                                    icon={PriorityIcon.questionCircle}
                                    onClick={this.onShowHelp}
                                />
                                <Separator align="horizontal" componentClasses="separator" />
                                <Icon
                                    skin="standart"
                                    icon={PriorityIcon.closeBig}
                                    onClick={this.onPanelClose}
                                />
                            </Flex>
                        </Flex>

                        <Flex justifyContent="flex-start">
                            <SearchesDropdownComp searchesStore={searchesStore} />
                        </Flex>
                    </Flex>

                    {recommendedSearch && (
                        <RecommendedSearch
                            form={currentForm}
                            searchesStore={searchesStore}
                            recommendedSearch={recommendedSearch}
                            showRecommendedSearch={this.state.showRecommendedSearch}
                            toggleRecommendedSearch={this.toggleRecommendedSearch}
                        />
                    )}

                    {generatorSelected && (
                        <React.Fragment>
                            <div className="top">
                                <Flex componentClasses="top-row">
                                    <CheckBox
                                        checked={generatorSelected.ignoreCase}
                                        onChange={this.toggleIgnoreCase}
                                    >
                                        <Label
                                            tag="span"
                                            skin="paragraph-2"
                                            textColor="primary-700"
                                        >
                                            {getLocalizedText(texts.caseSensitive)}
                                        </Label>
                                    </CheckBox>

                                    <Label tag="span" skin="label-1" textColor="secondary-500">
                                        {getLocalizedText(texts.sortHint)}
                                    </Label>
                                </Flex>
                            </div>

                            {isAISearchSupported ? (
                                <AISearchBlock
                                    key={this.state.searchKey}
                                    showAiSearch={this.showAiSearch}
                                    handleSearch={this.handleAiSearch}
                                    isLoading={aiSearchLoading}
                                    hasError={aiSearchError}
                                    aiSearchVisible={this.state.aiSearch}
                                    isEmptyResult={aiSearchNoResults && !hasFilters}
                                    monitorInputButton={
                                        AIAdvancedSearchMonitoring.searchWithAIInput
                                    }
                                />
                            ) : null}

                            {!aiSearchLoading ? (
                                <FiltersList
                                    selectedSearch={searchesStore.generatorSelected}
                                    currentForm={currentForm}
                                    onDropdownClose={this.focusSearchBtn}
                                    autoFocus={!this.state.aiSearch}
                                    hideRecommendedSearch={this.hideRecommendedSearch}
                                />
                            ) : null}

                            <SearchesFooter
                                closePanel={this.hide}
                                selected={searchesStore.generatorSelected}
                                searchBtnRef={this.searchBtnRef}
                                onClear={this.onClear}
                                onApply={() => (this.clearedSavedFilters = false)}
                                isAISearch={this.state.aiSearch}
                            />
                        </React.Fragment>
                    )}
                </div>
            </Drawer>
        );
    }
}
